import React, { useEffect, useMemo, useState, useContext } from "react";
import { AxiosError } from "axios";
import { queryCache, QueryKey, useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  Button,
  Dialog,
  Loader,
  showNotification,
} from "fe-shared/src/components";
import UserContext from "contexts/userContext";
import useDialog from "fe-shared/src/hooks/useDialog";
import useTenantOptions from "fe-shared/src/hooks/useTenantOptions";
import useInvoiceApprovalThresholds from "hooks/useInvoiceApprovalThresholds";
import { CheckIcon } from "fe-shared/src/components/Icons";
import { ApiError } from "fe-shared/src/types/common";
import { Invoice } from "fe-shared-transactions/src/types";
import { DialogHeader } from "fe-shared-transactions/src/components";
import InvoiceAdjustmentDialog from "components/InvoiceAdjustmentDialog";

import { actions, ActionConfig, InvoiceAction } from "../modules/config";

import * as api from "../modules/api";

import styles from "../InvoicesDetailsView.module.scss";

interface Props {
  invoice: Invoice;
  activitiesQueryKey?: QueryKey;
  invoiceDetailsQueryKey?: QueryKey;
}

type ToastIcons = {
  [key in InvoiceAction]?: React.ElementType;
};

const { FirstApprove, SecondApprove, PostToGP, Cancel, Adjust } = InvoiceAction;

const toastIcons: ToastIcons = {
  [SecondApprove]: CheckIcon,
};

const InvoiceActionsContainer = (props: Props) => {
  const { invoice, activitiesQueryKey, invoiceDetailsQueryKey } = props;
  const history = useHistory();
  const { t } = useTranslation();
  const [ tenantOptions ] = useTenantOptions();
  const currentUser = useContext(UserContext);
  const invoiceApprovalAmountThresholds  = useInvoiceApprovalThresholds();
  const [activeAction, setActiveAction] = useState(actions[0]);
  const {
    isDialogActive,
    isDialogRendered,
    openDialog,
    closeDialog,
  } = useDialog();
  const { name: activeActionName } = activeAction;
  const isAdjustmentAction = activeActionName === Adjust;

  const visibleActions = useMemo(
    () =>
      actions.filter((action) =>
        action.isVisible(
          invoice,
          invoiceApprovalAmountThresholds,
          currentUser?.permissions,
          tenantOptions.isGPEnabled
        )
      ),
    [invoice, currentUser, invoiceApprovalAmountThresholds]
  );

  const getMutationConfig = (action: InvoiceAction) => ({
    onSuccess: () => {
      const iconComponent = toastIcons[action];

      queryCache.invalidateQueries(invoiceDetailsQueryKey);
      queryCache.invalidateQueries(activitiesQueryKey);
      showNotification(
        t(`invoiceActions.${action}.toastMessage`),
        undefined,
        iconComponent
      );
    },
  });

  const mutations = {
    [FirstApprove]: useMutation<unknown, AxiosError<ApiError>>(
      () => api.approveInvoiceFirstTime({ invoiceId: invoice.invoiceId }),
      getMutationConfig(FirstApprove)
    ),
    [SecondApprove]: useMutation<unknown, AxiosError<ApiError>>(
      () => api.approveInvoiceSecondTime({ invoiceId: invoice.invoiceId }),
      getMutationConfig(SecondApprove)
    ),
    [PostToGP]: useMutation<unknown, AxiosError<ApiError>>(
      () => api.postInvoiceToGP({ invoiceId: invoice.invoiceId }),
      getMutationConfig(PostToGP)
    ),
    [Cancel]: useMutation<unknown, AxiosError<ApiError>>(
      () => api.cancelInvoice({ invoiceId: invoice.invoiceId }),
      getMutationConfig(Cancel)
    ),
    [Adjust]: useMutation<unknown, AxiosError<ApiError>>(
      () => Promise.resolve(),
      getMutationConfig(Adjust)
    ),
  };

  const [applyAction, { isLoading, isSuccess }] = mutations[activeActionName];

  const dialogTitle = (
    <DialogHeader
      type="Invoice"
      title={t(`invoiceActions.${activeActionName}.title`)}
      number={invoice.invoiceNumber}
      translationRoot="invoices"
    />
  );

  const dialogActions = [
    <Button
      key="1"
      id={`invoice-action-dialog-${activeActionName}-button`}
      onClick={() => applyAction()}
      {...activeAction?.buttonProps}
      {...activeAction?.dialogButtonProps}
    >
      {t(`invoiceActions.${activeActionName}.modalbuttonTitle`)}
    </Button>,
    <Button
      key="2"
      variant="outlined"
      onClick={closeDialog}
      id="invoice-action-dialog-cancel-button"
    >
      {t("cancel")}
    </Button>,
  ];

  useEffect(() => {
    if (!isLoading && isSuccess) {
      closeDialog();
      history.push("/invoices");
    }
  }, [isLoading, isSuccess, history, closeDialog]);

  const applyDialogAction = (action: ActionConfig) => {
    setActiveAction(action);
    openDialog();
  };

  return (
    <>
      <div className={styles.actions}>
        {visibleActions.map((action) => {
          const { name, buttonProps, hasConfirmationDialog } = action;
          const [applyInstantAction] = mutations[name];

          return (
            <Button
              key={name}
              id={`invoice-action-${name}-button`}
              className={styles.button}
              disabled={isLoading}
              onClick={() => {
                if (hasConfirmationDialog) {
                  applyDialogAction(action);
                } else {
                  applyInstantAction();
                }
              }}
              {...buttonProps}
            >
              {t(`invoiceActions.${name}.buttonLabel`)}
            </Button>
          );
        })}
      </div>
      {isAdjustmentAction && isDialogRendered && (
        <InvoiceAdjustmentDialog
          invoice={invoice}
          isActive={isDialogActive}
          onClose={closeDialog}
          activitiesQueryKey={activitiesQueryKey}
          invoiceDetailsQueryKey={invoiceDetailsQueryKey}
        />
      )}
      {!isAdjustmentAction && isDialogRendered && (
        <Dialog
          modal
          title={dialogTitle}
          actions={dialogActions}
          isActive={isDialogActive}
          onClose={closeDialog}
        >
          {isLoading && <Loader isBlocking />}
          {t(`invoiceActions.${activeActionName}.modalMessage`)}
        </Dialog>
      )}
    </>
  );
};

export default InvoiceActionsContainer;
